import { ConnectionPositionPair } from '@angular/cdk/overlay';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';

import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import {
  ClientToolsRegistry,
  TellentAssistantConfigService,
  TellentAssistantFacade,
  TellentAssistantThreadsFacade,
} from '@core/tellent-assistant-logic';
import {
  TellentAssistantEmptyStateComponent,
  TellentAssistantHeaderComponent,
  TellentAssistantInputComponent,
  TellentAssistantMessageComponent,
  TellentAssistantThreadDrawerComponent,
  TellentAssistantThinkingIndicatorComponent,
} from '@core/tellent-assistant-ui';
import { AppKind, ORG_GUID } from '@core/tellent-types';
import { injectTellentAppUrlBuilder } from '@core/tellent-util';
import {
  ConnectedOverlayDirective,
  FIXED_OVERLAY_LEVEL,
  OverlayOriginDirective,
  WindowRef,
} from '@core/user-interface-legacy';

@Component({
  selector: 'tellent-assistant-chat-panel',
  imports: [
    TellentAssistantHeaderComponent,
    TellentAssistantEmptyStateComponent,
    TellentAssistantInputComponent,
    TellentAssistantThreadDrawerComponent,
    TellentAssistantMessageComponent,
    ConnectedOverlayDirective,
    OverlayOriginDirective,
    TellentAssistantThinkingIndicatorComponent,
    TranslatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './tellent-assistant-chat-panel.component.html',
  styleUrl: './tellent-assistant-chat-panel.component.less',
  host: {
    '[class.expanded]': 'expanded()',
    '[class.first-paint-done]': 'firstPaintDone()',
  },
})
export class TellentAssistantChatPanelComponent {
  private readonly i18n = inject(I18nService);
  private readonly windowRef = inject(WindowRef);
  private readonly orgGuid = inject(ORG_GUID, { optional: true });
  private readonly urlBuilder = injectTellentAppUrlBuilder();
  private readonly drawerOverlay = viewChild<ConnectedOverlayDirective>('drawerOverlay');
  private readonly clientToolsRegistry = inject(ClientToolsRegistry);

  protected readonly facade = inject(TellentAssistantFacade);
  protected readonly threadsFacade = inject(TellentAssistantThreadsFacade);
  protected readonly config = inject(TellentAssistantConfigService);
  protected readonly drawerOpen = signal<boolean>(false);
  protected readonly firstPaintDone = signal<boolean>(false);
  protected readonly defaultTitle = this.i18n.translate('tlc.tellent_assistant.chat_panel.title');
  protected readonly currentThread = computed(() => {
    const id = this.facade.currentThreadId();
    if (!id) return null;
    return this.threadsFacade.threads().find(t => t.id === id) ?? null;
  });
  protected readonly title = computed(() => this.currentThread()?.title || this.defaultTitle);
  protected readonly currentThreadPinned = computed(() => this.currentThread()?.pinned ?? false);
  protected readonly drawerPositions = [
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'top' },
  ] satisfies ConnectionPositionPair[];

  public readonly expanded = input.required<boolean>();
  public readonly close = output<void>();
  public readonly expand = output<void>();
  public readonly collapse = output<void>();

  public readonly renderToolNames = this.clientToolsRegistry.ids;

  constructor() {
    afterNextRender(() => this.firstPaintDone.set(true));
  }

  public readonly isDeleting = (id: string): boolean => this.threadsFacade.isThreadDeleting(id);

  public readonly resolveRenderComponent = (
    toolName: string,
    _args: unknown,
    status: 'running' | 'done' | 'error',
  ) =>
    this.clientToolsRegistry.shouldRender(toolName, status)
      ? this.clientToolsRegistry.byId(toolName)?.component
      : undefined;

  public deleteThread(id: string): void {
    this.threadsFacade.deleteThread(id);
  }

  protected togglePin(): void {
    const thread = this.currentThread();
    if (!thread) return;
    this.threadsFacade.setThreadPinned(thread.id, !thread.pinned);
  }

  protected onDrawerAttach(): void {
    const host = this.drawerOverlay()?.overlayRef?.hostElement;
    if (host) host.style.zIndex = `${FIXED_OVERLAY_LEVEL + 1}`;
  }

  protected send(text: string): void {
    this.facade.send(text);
  }

  protected newThread(): void {
    this.facade.newThread();
  }

  protected selectThread(id: string): void {
    this.facade.switchThread(id);
  }

  protected openHelpCenter(): void {
    const url = this.i18n.translate('app.support_fab.help_center_settings_url');
    this.windowRef.nativeWindow.open(url, '_blank');
  }

  protected openIntelligenceSettings(): void {
    if (!this.orgGuid) return;
    const url = this.urlBuilder(AppKind.TellentAdmin, { id: 'intelligence' }, this.orgGuid);
    this.windowRef.nativeWindow.location = url;
  }
}
